import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import css from "./Tabs.module.css";

const TabsRoot = React.forwardRef(
  (
    {
      value,
      defaultValue,
      onValueChange,
      orientation = "horizontal",
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BaseTabs.Root
      ref={ref}
      className={clsx(css.root, className)}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      orientation={orientation}
      {...rest}
    >
      {children}
    </BaseTabs.Root>
  ),
);

TabsRoot.displayName = "TabsRoot";

TabsRoot.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onValueChange: PropTypes.func,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  className: PropTypes.string,
  children: PropTypes.node,
};

const TabsList = React.forwardRef(({ className, children, ...rest }, ref) => (
  <BaseTabs.List ref={ref} className={clsx(css.list, className)} {...rest}>
    {children}
    <TabsIndicator />
  </BaseTabs.List>
));

TabsList.displayName = "TabsList";

TabsList.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const TabsTab = React.forwardRef(
  ({ value, disabled = false, className, children, ...rest }, ref) => (
    <BaseTabs.Tab
      ref={ref}
      value={value}
      disabled={disabled}
      className={clsx(css.tab, className)}
      {...rest}
    >
      {children}
    </BaseTabs.Tab>
  ),
);

TabsTab.displayName = "TabsTab";

TabsTab.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const TabsPanel = React.forwardRef(
  ({ value, keepMounted, className, children, ...rest }, ref) => (
    <BaseTabs.Panel
      ref={ref}
      value={value}
      keepMounted={keepMounted}
      className={clsx(css.panel, className)}
      {...rest}
    >
      {children}
    </BaseTabs.Panel>
  ),
);

TabsPanel.displayName = "TabsPanel";

TabsPanel.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  keepMounted: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const TabsIndicator = React.forwardRef(({ className, ...rest }, ref) => (
  <BaseTabs.Indicator
    ref={ref}
    className={clsx(css.indicator, className)}
    {...rest}
  />
));

TabsIndicator.displayName = "TabsIndicator";

TabsIndicator.propTypes = {
  className: PropTypes.string,
};

const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel,
  Indicator: TabsIndicator,
};

export default Tabs;
